import {DatePipe} from '@angular/common';
import {Component, Input} from '@angular/core';
import {
    ColumnFilter,
    DataTableColumnData,
    DataTableConfig,
    DataTablePaginatedDataSource,
    getErrorMessage,
    mapPaginatedOrNonPaginatedResponse,
    Organisation,
    TableAction
} from '@cas-hub/acabim-common-client';
import {MessageService} from 'primeng/api';
import {concat, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {OrganisationSeat} from '../../../../_models/organisation/organisation-seats.model';
import {OrganisationSeatsService} from '../../../../_services/organisation/organisation-seats.service';

interface OrganisationSeatEnhanced extends OrganisationSeat {
    readonly isTrial: boolean;
}

const enum UserSubscriptionsViewTableActions {
    MarkAsPurchased = 0,
    RemoveSeat = 1,
    ClearExpiryDate = 2
}

@Component({
    selector: 'acabim-admin-user-subscriptions-view',
    templateUrl: './user-subscriptions-view.component.html',
    styleUrls: ['./user-subscriptions-view.component.scss']
})
export class UserSubscriptionsViewComponent extends DataTablePaginatedDataSource<OrganisationSeatEnhanced> {

    @Input()
    public set selectedOrganisation(val: Organisation | undefined) {
        this._selectedOrganisation = val;
        if (val) {
            this.onRequestToRefreshData();
        }
    }

    public get selectedOrganisation(): Organisation | undefined {
        return this._selectedOrganisation;
    }

    public readonly actions: TableAction[] = [
        {id: UserSubscriptionsViewTableActions.MarkAsPurchased, name: 'Mark as Purchased'},
        {id: UserSubscriptionsViewTableActions.RemoveSeat, name: 'Remove Seat'},
        {id: UserSubscriptionsViewTableActions.ClearExpiryDate, name: 'Remove expiry date'}
    ];

    public readonly columnData: DataTableColumnData[] = [
        {displayName: 'Seat Id', objectPropertyName: 'seatId'},
        {displayName: 'Number Of Seats', objectPropertyName: 'numberOfSeats', editable: true},
        {displayName: 'Usage Count', objectPropertyName: 'usageCount'},
        {displayName: 'Remaining Seats', objectPropertyName: 'remainingSeats'},
        {displayName: 'Remaining Days', objectPropertyName: 'remainingDays'},
        {displayName: 'Expire Date', objectPropertyName: 'expireDate', editable: true, type: 'date'},
        {displayName: 'Ordered by', objectPropertyName: 'orderedUser.name'},
        {displayName: 'Ordered Date', objectPropertyName: 'orderedDate'},
        {displayName: 'Purchase Date', objectPropertyName: 'purchaseDate'},
        {displayName: 'Is Trial', objectPropertyName: 'isTrial'}
    ];

    public readonly columnFilters: ColumnFilter[] = [];

    public readonly config: DataTableConfig = {
        globalFilterFields: [],
        hasActions: true,
        hasCheckbox: true,
        hasDetailsButton: false,
        hasFilters: false,
        isExportEnabled: false,
        isLazyLoadingEnabled: true,
        isPaginationEnabled: true,
        isSearchEnabled: false,
        searchPlaceHolder: '',
        tableHeading: '',
        emptyMessage: 'No Seat orders found'
    };

    private _selectedOrganisation?: Organisation;

    private readonly seatNormaliser = (seat: OrganisationSeat): OrganisationSeatEnhanced => {
        return {
            ...seat,
            orderedDate: this.datePipe.transform(new Date(seat.orderedDate), 'dd MMMM yyyy') as string,
            purchaseDate: seat.purchaseDate ? this.datePipe.transform(new Date(seat.purchaseDate), 'dd MMMM yyyy') as string : undefined,
            expireDate: seat.expireDate ? this.datePipe.transform(new Date(seat.expireDate), 'dd MMMM yyyy') as string : undefined,
            isTrial: !seat.purchaseDate
        };
    };

    public constructor(private readonly seatsService: OrganisationSeatsService,
                       private readonly messageService: MessageService,
                       private readonly datePipe: DatePipe) {
        super((pagination) => {
            if (this.selectedOrganisation) {
                return this.seatsService.getSeatOrders(pagination, this.selectedOrganisation.id)
                    .pipe(map(seat => mapPaginatedOrNonPaginatedResponse(seat, this.seatNormaliser)));
            }

            return null;
        });
    }

    public onAction(action: TableAction): void {
        if (this.selectedData && this.selectedOrganisation) {
            const subscriptions: Promise<any>[] = [];

            for (const seat of this.selectedData) {
                switch (action.id) {
                    case UserSubscriptionsViewTableActions.MarkAsPurchased:
                        if (!seat.purchaseDate) {
                            subscriptions.push(this.seatsService.confirmPurchaseOrder(seat.seatId, this.selectedOrganisation.id).toPromise());
                        }
                        break;

                    case UserSubscriptionsViewTableActions.RemoveSeat:
                        subscriptions.push(this.seatsService.removePurchaseOrder(seat.seatId, this.selectedOrganisation.id).toPromise());
                        break;

                    case UserSubscriptionsViewTableActions.ClearExpiryDate:
                        if (seat.expireDate) {
                            subscriptions.push(this.seatsService.changeExpireDate(seat.seatId, this.selectedOrganisation.id, undefined).toPromise());
                        }
                        break;
                }
            }

            if (subscriptions.length > 0) {
                Promise.all(subscriptions).then(_ => this.onRequestToRefreshData())
                    .catch(error => this.messageService.add(getErrorMessage(error, 'Error', 'user-subscriptions-view-component')));
            }
        }
    }

    public onDataExpanded(value: OrganisationSeat): void {
    }

    public onNewTrialSeatCreated(seat: OrganisationSeat): void {
        this.rowData.push(this.seatNormaliser(seat));
    }

    public onSeatDataChanged(change: OrganisationSeat): void {
        if (this.selectedOrganisation) {
            const originalSeat = this.rowData.find(s => s.seatId === change.seatId);
            if (originalSeat) {
                const tasks: Observable<any>[] = [];
                if (originalSeat.numberOfSeats !== change.numberOfSeats) {
                    tasks.push(this.seatsService.updatePurchaseOrder(change.seatId, change.numberOfSeats, this.selectedOrganisation.id));
                }

                if (originalSeat.expireDate !== change.expireDate) {
                    tasks.push(this.seatsService.changeExpireDate(change.seatId, this.selectedOrganisation.id, change.expireDate));
                }

                if (tasks.length > 0) {
                    // Since we are working on the same seat, it is important to execute the calls one after the other to reduce concurrency issues
                    concat(...tasks).subscribe(_ => this.onRequestToRefreshData(),
                        error => this.messageService.add(getErrorMessage(error, 'Change seat error', 'user-subscriptions-view-component')));
                }
            }
        }
    }
}
